import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIsPro } from 'hooks/useIsPro';
import cn from 'classnames';

import billingIcon from 'assets/billing.svg';

import HelpButton from 'Components/HelpButton';

import CreateReview from 'Pages/TeamDashboard/CreateReview';
import DashboardHeader from 'Pages/TeamDashboard/Home/components/DashboardHeader';
import Sidebar from 'Pages/TeamDashboard/Home/components/Sidebar';

import { closeCreateReviewModal } from 'Redux/Actions/createReviewActions';

import styles from './styles.module.scss';

const Settings = () => {
  const dispatch = useDispatch();
  const [activeSection, setActiveSection] = useState('billing');
  const [showTooltip, setShowTooltip] = useState(false);

  const reviewData = useSelector(state => state.designReview.reviewCount);
  const { isOpen: isCreateReviewOpen, selectedReview } = useSelector(state => state.createReview);
  const isPro = useIsPro();

  const reviewsRemaining = reviewData?.reviews_remaining || 0;
  const totalCredits = isPro ? 60 : 20;
  const creditPercentage = totalCredits > 0 ? (reviewsRemaining / totalCredits) * 100 : 0;

  // Handle closing review popup
  const handleCloseReview = () => {
    dispatch(closeCreateReviewModal());
  };

  return (
    <div className={styles['settings-container']}>
      <div className="min-h-screen bg-gray-50 grid gap-0 flex w-full">
        <Sidebar />
        <div className="flex flex-1 bg-gray-50 p-2 pl-0 max-h-screen">
          <div className="flex flex-1 flex-col overflow-auto rounded-lg bg-white shadow-figma-sm relative">
            <DashboardHeader
              title="Members"
            />

            <div className="flex flex-1 pl-40 items-center">

              <div
                className={cn(
                  'flex overflow-auto rounded-xl border border-[#E1E4E8] w-11/12',
                  'max-w-[960px] h-4/5 max-h-[720px]'
                )}
              >
                {/* Left Settings Sidebar */}
                <div className={styles['settings-sidebar']}>
                  <div className={styles.section}>
                    <h3 className={styles.heading}>
                      <span className={styles.text}>
                        Workspace
                      </span>
                    </h3>

                    <div className={styles.options}>
                      {/* Billing */}
                      <button
                        className={`${styles['nav-link']} ${activeSection === 'billing' ? styles.active : ''}`}
                        onClick={() => setActiveSection('billing')}
                      >
                        <div className={styles['nav-icon']}>
                          <img src={billingIcon} alt="Billing" />
                        </div>
                        <span className={styles['nav-text']}>
                          Billing
                        </span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Main Content */}
                <div className={styles['settings-main']}>
                  {activeSection === 'billing' && (
                    <div className={styles['billing-section']}>
                      {/* Active plan heading */}
                      <div className={styles['section-heading']}>
                        <span className={styles['heading-text']}>
                          Active plan
                        </span>
                        <span>
                          {isPro ? '(Pro tier)' : '(Free tier)'}
                        </span>
                      </div>

                      {/* Usage section */}
                      <div className={styles.usage}>
                        <div className={styles['usage-heading']}>
                          <div className={styles.label}>
                            Plan usage Limits
                          </div>
                          <div
                            className={styles['help-icon']}
                            onMouseEnter={() => setShowTooltip(true)}
                            onMouseLeave={() => setShowTooltip(false)}
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                              <circle cx="8" cy="8" r="6.5" stroke="#8F97A5" strokeWidth="1.5" />
                              <path d="M8 11V8M8 5H8.01" stroke="#8F97A5" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>

                            {/* Tooltip */}
                            {showTooltip && (
                              <div className={styles.tooltip}>
                                <div className={styles['tooltip-content']}>
                                  <div className={styles.breakdown}>
                                    <div className={styles.item}>
                                      <span className={styles['item-label']}>
                                        Single screen audit
                                      </span>
                                      <span className={styles['item-value']}>
                                        1 credit
                                      </span>
                                    </div>
                                    <div className={styles.item}>
                                      <span className={styles['item-label']}>
                                        Prototype
                                      </span>
                                      <span className={styles['item-value']}>
                                        1 credit
                                      </span>
                                    </div>
                                    <div className={styles.item}>
                                      <span className={styles['item-label']}>
                                        Flow audit
                                      </span>
                                      <span className={styles['item-value']}>
                                        2 credits
                                      </span>
                                    </div>
                                  </div>
                                  <div className={styles.divider} />
                                  <div className={styles['tooltip-footer']}>
                                    Resets monthly
                                  </div>
                                </div>
                                <div className={styles['tooltip-arrow']} />
                              </div>
                            )}
                          </div>
                        </div>

                        <div className={styles['progress-bar-container']}>
                          <div className={styles['progress-labels']}>
                            <div className={styles['credits-available']}>
                              <span
                                className={`${styles.remaining} ${
                                  reviewsRemaining === 0 ? styles['remaining-zero'] : ''
                                }`}
                              >
                                {reviewsRemaining}
                              </span>
                              <span className={styles['credit-total']}>
                                /
                                {' '}
                                {totalCredits}
                                {' '}
                                credits
                              </span>
                            </div>
                            <div className={styles.percentage}>
                              {Math.round(creditPercentage)}
                              % left
                            </div>
                          </div>

                          <div className={styles.bar}>
                            <div className={styles['bar-background']} />
                            <div
                              className={`${styles['bar-fill']} ${creditPercentage <= 0 ? styles.hidden : ''}`}
                              style={{ right: `${100 - Math.min(100, creditPercentage)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <HelpButton />
          </div>
        </div>
      </div>

      {/* CreateReview Modal */}
      {isCreateReviewOpen && (
        <div className="fixed inset-0 z-50">
          <CreateReview
            onClose={handleCloseReview}
            existingReviewData={selectedReview}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
